// ---------------------------------------------------------------------------
// Cookie consent — the ONE reader and writer of the visitor's choice (B98).
//
// ReCurr's published Cookie Policy (§3) promises a first-visit banner that
// accepts or declines the non-essential categories, and a "cookie settings"
// link that changes the choice later. This module is the state behind both;
// components/CookieConsentBanner.tsx is the banner.
//
// WHAT THE CHOICE GOVERNS — measured 2026-10-10 in a real Chromium on
// production, the only two non-essential things ReCurr's own pages do:
//   · ANALYTICS — Cloudflare Web Analytics (utils/cloudflareAnalytics.ts). It
//     sets no cookie, but it reports every page view, so the policy's
//     "analytics" category is exactly this.
//   · FUNCTIONAL — the dashboard remembering a notice the person dismissed
//     (announcements, coachmarks, the onboarding widget). The policy names
//     "whether you have dismissed a notice" as functional, so it is
//     `preferenceStorage()` below: across visits on Accept, the tab only on
//     Decline (founder's ruling, 2026-10-10).
// Everything else the SPA stores is strictly necessary (the session, the CSRF
// cookie, a cart, a checkout in progress) and is not asked about.
//
// WHERE THE CHOICE LIVES — in the visitor's browser only (ruling 3: no server
// log, no migration), as a first-party cookie on the parent domain. Not
// localStorage: recurr.co.za and app.recurr.co.za are different origins, so a
// visitor who answered on the marketing site would be asked again the moment
// they signed in. The server never reads it. It carries the Cookie Policy
// version it answered, so a later version asks again, and expires after a
// year, when it asks again too.
// ---------------------------------------------------------------------------

export type CookieChoice = 'accepted' | 'declined';

export interface CookieConsent {
  choice: CookieChoice;
  /** The Cookie Policy version the visitor answered; null if it could not be read. */
  policyVersion: number | null;
  /** ISO timestamp of the answer. */
  decidedAt: string;
}

export const COOKIE_CONSENT_NAME = 'recurr_cookie_consent';
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;
const PARENT_DOMAIN = 'recurr.co.za';

/** Fired on `window` whenever the choice changes, and to reopen the banner. */
export const COOKIE_CONSENT_CHANGED = 'recurr:cookie-consent-changed';
export const OPEN_COOKIE_SETTINGS = 'recurr:open-cookie-settings';

function readCookie(name: string): string | null {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

/** `Domain=recurr.co.za` on either ReCurr hostname, so one answer covers both; host-only elsewhere. */
function domainAttribute(hostname: string): string {
  const h = hostname.toLowerCase();
  return h === PARENT_DOMAIN || h.endsWith(`.${PARENT_DOMAIN}`) ? `; Domain=${PARENT_DOMAIN}` : '';
}

export function readCookieConsent(): CookieConsent | null {
  const raw = readCookie(COOKIE_CONSENT_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (parsed.choice !== 'accepted' && parsed.choice !== 'declined') return null;
    return {
      choice: parsed.choice,
      policyVersion: typeof parsed.policyVersion === 'number' ? parsed.policyVersion : null,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : '',
    };
  } catch {
    return null;
  }
}

/** True only after an explicit Accept — no answer is a Decline. */
export function hasAcceptedCookies(): boolean {
  return readCookieConsent()?.choice === 'accepted';
}

/**
 * Whether the banner should ask. No answer asks; an answer to an OLDER policy
 * version asks again. An unknown live version never re-asks someone who has
 * answered (a failed fetch must not nag).
 */
export function needsCookieAnswer(consent: CookieConsent | null, livePolicyVersion: number | null): boolean {
  if (!consent) return true;
  if (livePolicyVersion === null || consent.policyVersion === null) return false;
  return consent.policyVersion < livePolicyVersion;
}

export function writeCookieConsent(choice: CookieChoice, policyVersion: number | null): CookieConsent {
  const consent: CookieConsent = { choice, policyVersion, decidedAt: new Date().toISOString() };
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${COOKIE_CONSENT_NAME}=${encodeURIComponent(JSON.stringify(consent))}; Path=/; Max-Age=${MAX_AGE_SECONDS}` +
      `; SameSite=Lax${secure}${domainAttribute(window.location.hostname)}`;
  } catch {
    /* cookies blocked — the banner still hides for this page view */
  }
  moveFunctionalPreferences(choice === 'accepted' ? 'toLocal' : 'toSession');
  try {
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED, { detail: consent }));
  } catch { /* no window (tests) */ }
  return consent;
}

/** The footer's "Cookie settings" link and the Privacy page call this. */
export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS));
}

// ---------------------------------------------------------------------------
// Functional preferences — what the dashboard remembers that a person dismissed.
// ---------------------------------------------------------------------------

/** Every key the functional category writes. A new "remember I dismissed this" key belongs here. */
export function isFunctionalPreferenceKey(key: string): boolean {
  return (
    key === 'announcementsBanner.dismissed' ||
    key === 'onboarding_launch_dismissed' ||
    (key.startsWith('coachmark:') && key.endsWith(':dismissed'))
  );
}

/** A Storage that forgets — for a browser that refuses both (private mode, blocked site data). */
class MemoryStorage implements Storage {
  private map = new Map<string, string>();
  get length(): number { return this.map.size; }
  clear(): void { this.map.clear(); }
  getItem(key: string): string | null { return this.map.has(key) ? this.map.get(key)! : null; }
  key(index: number): string | null { return Array.from(this.map.keys())[index] ?? null; }
  removeItem(key: string): void { this.map.delete(key); }
  setItem(key: string, value: string): void { this.map.set(key, String(value)); }
}
const memoryFallback = new MemoryStorage();

function storageOrNull(kind: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    const s = window[kind];
    s.getItem('__probe__');
    return s;
  } catch {
    return null;
  }
}

/**
 * Where a functional preference is kept: across visits after Accept, for this
 * tab only otherwise. Every "remember that I dismissed this" read and write
 * goes through here — never `localStorage` directly.
 */
export function preferenceStorage(): Storage {
  const kind = hasAcceptedCookies() ? 'localStorage' : 'sessionStorage';
  return storageOrNull(kind) ?? memoryFallback;
}

/**
 * On a change of mind the preferences already kept move with it: Decline
 * clears them from localStorage (keeping them for this tab, so nothing just
 * dismissed pops back), Accept keeps what this tab dismissed for later visits.
 */
function moveFunctionalPreferences(direction: 'toLocal' | 'toSession'): void {
  const from = storageOrNull(direction === 'toLocal' ? 'sessionStorage' : 'localStorage');
  const to = storageOrNull(direction === 'toLocal' ? 'localStorage' : 'sessionStorage');
  if (!from) return;
  const keys: string[] = [];
  for (let i = 0; i < from.length; i += 1) {
    const k = from.key(i);
    if (k && isFunctionalPreferenceKey(k)) keys.push(k);
  }
  for (const k of keys) {
    const v = from.getItem(k);
    try { if (to && v !== null) to.setItem(k, v); } catch { /* quota */ }
    from.removeItem(k);
  }
}

// ---------------------------------------------------------------------------
// Where the banner speaks
// ---------------------------------------------------------------------------

/**
 * Pages where the visitor deals with a MERCHANT, not ReCurr (ruling 2; Cookie
 * Policy §6): the merchant's public pages, signup link, knowledge base, store,
 * checkout, subscribe and settle-up links, and the signed invoice and quote
 * links a merchant emails their customer. `?merchant=` legal documents are
 * the merchant's too.
 */
const MERCHANT_SCOPED_PREFIXES = ['/p/', '/store', '/subscribe/', '/pay/', '/checkout/', '/invoice/', '/quote/'];

export function isCookieBannerPath(pathname: string, search: string): boolean {
  if (new URLSearchParams(search).has('merchant')) return false;
  return !MERCHANT_SCOPED_PREFIXES.some((p) => pathname === p.replace(/\/$/, '') || pathname.startsWith(p));
}
